"use client";
import ModuleLayout from "@/components/layout/ModuleLayout";
import { ContractRowSkeleton } from "@/components/pages/contracts/ContractRowSkeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { FormatCurrency } from "@/lib/utils/ numbers";
import { ListOrder, ListOrderBy } from "@/types/listing";
import { Flex, Text } from "@radix-ui/themes";
import { format, formatDuration, intervalToDuration } from "date-fns";
import {
  ArrowUpDown,
  Calendar,
  CornerDownRight,
  FilePlus,
  FilterIcon,
  MoreHorizontal,
  SearchIcon,
} from "lucide-react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { useDebounce } from "use-debounce";
import { Client, ContractStats } from "../../../types/models";
import {
  ContractFilterBy,
  DeleteContract,
  GetContracts,
  GetContractsTotalCount,
} from "../../actions/contracts/actions";

const ITEMS_TO_SHOW = 5;

type Page = {
  id: number;
  offset: number;
  isActive: boolean;
};

type ContractWithCounts = ContractStats & {
  clients: Client;
};

export default function ListContractsComponent() {
  const searchParams = useSearchParams();
  const router = useRouter();

  const [statusFilter, setStatusFilter] = useState<string>("");
  const [deleteAlert, setDeleteAlert] = useState(false);
  const [deletedId, setDeletedId] = useState<string>("");
  const [search, setSearch] = useState("");
  const [searchDebounced] = useDebounce(search, 500);
  const [offset, setOffset] = useState<number>();
  const [limit, setLimit] = useState<number>();
  const [order, setOrder] = useState<string>();
  const [orderBy, setOrderBy] = useState<string>();
  const [pages, setPages] = useState<Page[]>([]);
  const [activePage, setActivePage] = useState<Page>();
  const [pageCount, setPageCount] = useState(0);
  const [contractList, setContractList] = useState<ContractWithCounts[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const paramsOffset = Number(searchParams.get("offset")) || 0;
    const paramsLimit = Number(searchParams.get("limit")) || ITEMS_TO_SHOW;
    const paramsOrder = searchParams.get("order") || "asc";
    const paramsOrderBy = searchParams.get("orderBy") || "term";

    setOffset(paramsOffset);
    setLimit(paramsLimit);
    setOrder(paramsOrder);
    setOrderBy(paramsOrderBy);
  }, [searchParams]);

  useEffect(() => {
    const fetchTotal = async (search?: string) => {
      const { data: newTotal, error } = await GetContractsTotalCount({
        search,
      });
      if (error) {
        console.error(error);
      } else {
        setTotal(newTotal || 0);
        if (newTotal && limit) {
          setPageCount(Math.ceil((newTotal || 0) / limit));
        }
      }
    };

    (async () => {
      await fetchTotal(searchDebounced);
    })();
  }, [searchDebounced, limit, statusFilter]);

  const fetchContracts = async ({
    offset,
    limit,
    order,
    orderBy,
    search,
    filterBy,
    filterVal,
  }: {
    offset: number;
    limit: number;
    order: ListOrder;
    orderBy: ListOrderBy;
    search?: string;
    filterBy?: ContractFilterBy;
    filterVal?: string;
  }) => {
    setIsLoading(true);
    const { data, error } = await GetContracts({
      offset,
      limit,
      order: order as ListOrder,
      orderBy: orderBy as ListOrderBy,
      search: search,
      filterBy: filterBy,
      filterVal,
    });
    if (error) {
      console.error(error);
    } else {
      setContractList(data || []);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (offset === undefined || limit === undefined) return;

    (async () => {
      await fetchContracts({
        offset,
        limit,
        order: order as ListOrder,
        orderBy: orderBy as ListOrderBy,
        search: searchDebounced,
        filterBy: statusFilter ? "status" : undefined,
        filterVal: statusFilter,
      });
    })();
  }, [searchDebounced, limit, statusFilter, order, orderBy, offset]);

  useEffect(() => {
    const currentPage = pages.find((page) => page.offset === offset);
    setActivePage(currentPage || pages[0]);
  }, [offset, pages]);

  useEffect(() => {
    if (offset === undefined || limit === undefined) return;

    const pageArray = Array.from(
      { length: pageCount },
      (_, i) =>
        ({
          id: i + 1,
          offset: limit * i,
          isActive: limit * i === offset,
        }) as Page,
    );
    setPages(pageArray);
  }, [pageCount, limit]);

  const createQueryString = useCallback(
    (vals: { name: string; value: string }[]) => {
      const params = new URLSearchParams(searchParams.toString());
      vals.forEach(({ name, value }) => {
        params.set(name, value);
      });
      return params.toString();
    },
    [searchParams],
  );

  const getInitials = useCallback((name: string | undefined) => {
    if (!name) return "";
    const [first, last] = name.split(" ");
    if (!last && name.includes("-")) {
      return `${first[0]}${name.split("-")[1][0]}`.toUpperCase();
    }
    return `${first[0]}${last ? last[0] : ""}`.toUpperCase();
  }, []);

  const gotoPage = useCallback(
    (page: Page) => {
      if (page.id === activePage?.id || isLoading) return;
      if (offset === undefined || limit === undefined) return;
      setIsLoading(true);
      router.push(
        `/contracts?${createQueryString([
          { name: "offset", value: page.offset.toString() },
          { name: "limit", value: limit.toString() },
        ])}`,
      );
    },
    [activePage, isLoading, createQueryString, limit, router],
  );

  const applyOrder = useCallback(
    (newOrderBy: string) => {
      const newOrder =
        newOrderBy !== orderBy ? "asc" : order === "asc" ? "desc" : "asc";
      router.push(
        `/contracts?${createQueryString([
          { name: "orderBy", value: newOrderBy },
          { name: "order", value: newOrder },
        ])}`,
      );
    },
    [orderBy, order, createQueryString, router],
  );

  const previousPage = useCallback(() => {
    return activePage?.id === 1
      ? activePage
      : pages[activePage ? activePage.id - 2 : 1];
  }, [activePage, pages]);

  const nextPage = useCallback(() => {
    return activePage?.id === pages.length
      ? activePage
      : pages[activePage ? activePage.id : 1];
  }, [activePage, pages]);

  const attemptDeleteOfContract = useCallback((id: string) => {
    setDeleteAlert(true);
    setDeletedId(id);
  }, []);

  const deleteContract = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      if (deletedId === "") setDeleteAlert(false);
      DeleteContract(deletedId).then(() => {
        setDeletedId("");
        setDeleteAlert(false);
        if (offset === undefined || limit === undefined) return;
        fetchContracts({
          offset,
          limit,
          order: order as ListOrder,
          orderBy: orderBy as ListOrderBy,
          search: searchDebounced,
          filterBy: statusFilter ? "status" : undefined,
          filterVal: statusFilter,
        });
      });
    },
    [deletedId],
  );

  const capitalizeFirstLetter = useCallback((string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }, []);

  return (
    <ModuleLayout
      title="Contracts"
      subtitle="Manage your contracts"
      header={
        <div className="relative ml-auto flex-1 md:grow-0">
          <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search..."
            onChange={(e) => setSearch(e.target.value)}
            value={search}
            className="w-full rounded-lg bg-background pl-8 md:w-[200px] lg:w-[336px]"
          />
        </div>
      }
      footer={
        <>
          <Flex width="100%">
            <div className="text-xs text-muted-foreground">
              Showing{" "}
              <strong>
                {(offset || 0) + 1} -{" "}
                {total < Number(limit) + (offset || 0)
                  ? total
                  : Number(limit) + Number(offset)}
              </strong>{" "}
              of <strong>{total}</strong> contracts
            </div>
          </Flex>

          <Flex>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    className="cursor-pointer"
                    onClick={() => gotoPage(previousPage())}
                  />
                </PaginationItem>
                {pages.map((page) => (
                  <PaginationItem key={page.id}>
                    <PaginationLink
                      isActive={page.id === activePage?.id}
                      onClick={() => gotoPage(page)}
                      className="cursor-pointer"
                    >
                      {page.id}
                    </PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext
                    onClick={() => gotoPage(nextPage())}
                    className="cursor-pointer"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </Flex>
        </>
      }
      meta={
        <AlertDialog
          open={deleteAlert}
          onOpenChange={(open) => setDeleteAlert(open)}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your
                account and remove your data from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  variant="default"
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={(e) => deleteContract(e)}
                >
                  Delete Contract
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      }
    >
      <Flex className="w-full mb-4" justify="between" gap="3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="h-7 gap-1 text-sm">
              <FilterIcon className="h-3.5 w-3.5" />
              <span className="sr-only sm:not-sr-only">
                {statusFilter ? capitalizeFirstLetter(statusFilter) : "Status"}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuLabel>Filter by</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuRadioGroup
              value={statusFilter}
              onValueChange={(val) => setStatusFilter(val)}
            >
              <DropdownMenuRadioItem value="">All</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="active">
                Active
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="upcoming">
                Upcoming
              </DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="finished">
                Finished
              </DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          size="sm"
          variant="default"
          className="h-7 gap-1 text-sm"
          asChild
        >
          <Link href="/contracts/create">
            <FilePlus className="h-3.5 w-3.5" />
            <span className="sr-only sm:not-sr-only">Create</span>
          </Link>
        </Button>
      </Flex>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="hidden w-[50px] sm:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("client")}
                className="w-full justify-start"
              >
                Client
                {orderBy === "client" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/4  px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("alias")}
                className="w-full justify-start"
              >
                Alias
                {orderBy === "alias" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/10 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("rate")}
                className="w-full justify-start"
              >
                Rate
                {orderBy === "rate" && <ArrowUpDown className="ml-2 h-4 w-4" />}
              </Button>
            </TableHead>
            <TableHead className="w-1/12 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("timesheets_count")}
                className="w-full justify-start"
              >
                Timesheets
                {orderBy === "timesheets_count" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/12 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("total_hours")}
                className="w-full justify-start"
              >
                Total Hours
                {orderBy === "total_hours" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className="w-1/12 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("total_value")}
                className="w-full justify-start"
              >
                Value
                {orderBy === "total_value" && (
                  <ArrowUpDown className="ml-2 h-4 w-4" />
                )}
              </Button>
            </TableHead>
            <TableHead className=" w-1/3 hidden md:table-cell px-0">
              <Button
                variant="ghost"
                onClick={() => applyOrder("term")}
                className="w-full justify-start"
              >
                Term
                {orderBy === "term" && <ArrowUpDown className="ml-2 h-4 w-4" />}
              </Button>
            </TableHead>
            <TableHead className="w-[50px]">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading && <ContractRowSkeleton number={ITEMS_TO_SHOW} />}
          {!isLoading && contractList.length === 0 && (
            <TableRow>
              <TableCell colSpan={7} className="text-center">
                No contracts found
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            contractList.length !== 0 &&
            contractList.map((contract) => (
              <TableRow key={contract.id}>
                <TableCell className="hidden sm:table-cell">
                  <Avatar>
                    <AvatarImage src={contract.clients?.logo || ""} />
                    <AvatarFallback>
                      {getInitials(contract.clients?.name)}
                    </AvatarFallback>
                  </Avatar>
                </TableCell>
                <TableCell className="font-medium text-[14px] min-w-48">
                  <Flex direction="row" align="center" justify="start">
                    {contract.status === "upcoming" && (
                      <Badge
                        variant="outline"
                        className="mr-2 text-[10px] px-1.5 py-0.25"
                      >
                        Upcoming
                      </Badge>
                    )}
                    {contract.status === "active" && (
                      <Badge
                        variant="default"
                        className="mr-2 text-[10px] px-1.5 py-0.25"
                      >
                        Active
                      </Badge>
                    )}
                    {contract.status === "done" && (
                      <Badge
                        variant="secondary"
                        className="mr-2 text-[10px] px-1.5 py-0.25"
                      >
                        Done
                      </Badge>
                    )}

                    <Link href={`/contracts/${contract.id}`}>
                      {contract.alias}
                    </Link>
                  </Flex>
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {FormatCurrency({
                    number: contract.rate || 0,
                    currency: contract.currency,
                  })}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {contract?.timesheets_count}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {contract?.total_hours}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  {FormatCurrency({
                    number: contract?.total_value || 0,
                    currency: contract.currency,
                  })}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger>
                        <Flex direction="column">
                          <Flex direction="row" align="center">
                            <Calendar size={14} color="grey" className="mr-1" />
                            <Text color="gray" size="1" className="text-nowrap">
                              {format(
                                contract.start_date ? contract.start_date : "",
                                "do MMM y",
                              )}
                            </Text>
                          </Flex>
                          <Flex direction="row" align="center">
                            <CornerDownRight
                              size="14"
                              color="grey"
                              className="mr-1"
                            />
                            <Text color="gray" size="1" className="text-nowrap">
                              {contract.end_date &&
                                format(contract.end_date, "do MMM y")}
                              {!contract.end_date && "Ongoing"}
                            </Text>
                          </Flex>
                        </Flex>
                      </TooltipTrigger>
                      <TooltipContent>
                        {(() => {
                          const duration = intervalToDuration({
                            start: contract.start_date
                              ? contract.start_date
                              : "",
                            end: contract.end_date
                              ? contract.end_date
                              : Date.now(),
                          });

                          const formattedDuration = formatDuration(duration, {
                            format: ["years", "months", "days"],
                          });

                          return <Text size="2">{formattedDuration}</Text>;
                        })()}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell className="flex justify-center">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button aria-haspopup="true" size="icon" variant="ghost">
                        <MoreHorizontal className="h-4 w-4" />
                        <span className="sr-only">Toggle menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuItem asChild>
                        <Link href={`/contracts/${contract.id}`}>View</Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem>Edit</DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() =>
                          attemptDeleteOfContract(
                            contract.id ? contract.id : "",
                          )
                        }
                      >
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </Table>
    </ModuleLayout>
  );
}
